import React, { useState } from "react";
import { ChevronDown, ArrowRight } from "lucide-react";
import { Reveal, CountUp } from "../Animate";

// Brand palette used by every StudentsPublic subsection.
export const C = {
  green: "#006CFF",
  greenDeep: "#0F7C39",
  navy: "#0040A8",
  navyDeep: "#0A1F44",
  orange: "#FF8A00",
  purple: "#6C5CE7",
  ink: "#0F172A",
  paper: "#F5F7FA",
  rule: "#E5E7EB",
};

export function Pill({ children, color = C.green }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] font-bold tracking-widest uppercase px-3 py-1 rounded-full"
      style={{ background: `${color}15`, color }}>
      {children}
    </span>
  );
}

export function StatCard({ icon: Icon, value, prefix = "", suffix = "", label, color, delay = 0 }) {
  return (
    <Reveal delay={delay} className="group rounded-2xl border bg-white p-5 float-hover hover:shadow-lg"
      style={{ borderColor: C.rule }}>
      <div className="w-11 h-11 rounded-xl grid place-items-center transition-transform group-hover:scale-110 group-hover:rotate-3"
        style={{ background: `${color}15`, color }}>
        <Icon size={22} />
      </div>
      <div className="mt-4 font-display font-black text-2xl md:text-3xl tracking-tight" style={{ color }}
        data-testid={`metric-${label.toLowerCase().split(" ")[0]}`}>
        <CountUp value={value} prefix={prefix} suffix={suffix} />
      </div>
      <div className="mt-1 text-sm font-medium text-slate-600">{label}</div>
    </Reveal>
  );
}

export function FeatureCard({ icon: Icon, title, body, color, delay = 0 }) {
  return (
    <Reveal delay={delay} className="group rounded-2xl border bg-white p-6 float-hover hover:shadow-xl"
      style={{ borderColor: C.rule }}
      data-testid={`feature-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="w-12 h-12 rounded-xl grid place-items-center transition-all duration-300 group-hover:scale-110 group-hover:rotate-3"
        style={{ background: `${color}15`, color }}>
        <Icon size={24} strokeWidth={2} />
      </div>
      <h3 className="font-display font-bold text-lg mt-4" style={{ color: C.ink }}>{title}</h3>
      <p className="text-sm text-slate-600 mt-2 leading-relaxed">{body}</p>
    </Reveal>
  );
}

export function FAQ({ q, a, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-xl border bg-white transition-shadow hover:shadow-sm" style={{ borderColor: C.rule }}>
      <button onClick={() => setOpen(v => !v)}
        className="w-full text-left p-5 flex items-center justify-between gap-4"
        data-testid={`faq-${q.toLowerCase().split(" ").slice(0, 3).join("-")}`}>
        <span className="font-semibold text-sm md:text-base" style={{ color: C.ink }}>{q}</span>
        <ChevronDown size={18} className={`shrink-0 transition-transform ${open ? "rotate-180" : ""}`} style={{ color: C.green }} />
      </button>
      <div className={`grid transition-all duration-300 ${open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
        <div className="overflow-hidden">
          <div className="px-5 pb-5 text-sm text-slate-600 leading-relaxed">{a}</div>
        </div>
      </div>
    </div>
  );
}

export function Step({ n, icon: Icon, title, color }) {
  return (
    <div className="flex-1 min-w-[140px] flex flex-col items-center text-center" data-testid={`step-${n}`}>
      <div className="relative">
        <div className="w-16 h-16 rounded-2xl grid place-items-center text-white shadow-lg transition-transform hover:scale-110 hover:rotate-6"
          style={{ background: `linear-gradient(135deg, ${color} 0%, ${color}cc 100%)` }}>
          <Icon size={26} />
        </div>
        <span className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-white border text-[10px] font-bold grid place-items-center shadow"
          style={{ borderColor: C.rule, color: C.ink }}>{n}</span>
      </div>
      <div className="mt-3 font-bold text-sm" style={{ color: C.ink }}>{title}</div>
    </div>
  );
}

export function ArrowChevron() {
  return (
    <div className="hidden md:flex items-center text-slate-300" aria-hidden="true">
      <ArrowRight size={20} />
    </div>
  );
}
